import memoized from 'micro-memoize';
import { z } from 'zod';

import envVars from '../env.config.js';
import packageJson from '../package.json';

export const clientConfigSchema = z.object({
  port: z.number().min(1).nullish(),
  version: z.string().min(1),
  name: z.string().min(1),
  environment: z.string().min(1),
  locale: z.union([z.literal('en-GB'), z.literal('en-US'), z.literal('en-AU')]), // only supported locales
  currency: z.union([z.literal('GBP'), z.literal('USD')]), // only supported currencies
  pricesIncTax: z.boolean(),
  timeDurationsAreDecimalised: z.boolean(),
  description: z.string(),
  bffBaseUrl: z.string().startsWith('http'),
  tireEnablerBaseUrl: z.string().url(),
  industryEnablerBaseUrl: z.string().url(),
  haynesProEnablerBaseUrl: z.string().url(),
  EpicorIntegrationEnabled: z.boolean(),
  HaynesProIntegrationEnabled: z.boolean(),
  paymentEnablerBaseUrl: z.string().url(),
  TireEnablerIntegrationMode: z
    .enum(['USER_CHOICE', 'ATD', 'TIRE_CONNECT'])
    .nullish(),
  RefundsFeatureEnabled: z.boolean(),
  RefundsV2Enabled: z.boolean(),
  RolesAndPermissionsEnabled: z.boolean(),
  datadog: z.object({
    enabled: z.boolean(),
    environment: z.string().min(1), // NOTE: to identify your personal env, set to custom value e.g. 'local-pete'
    serviceName: z.string().min(1),
    applicationId: z.string().min(1),
    clientToken: z.string().min(1),
    allowSessionRecording: z.boolean(),
    allowUserTracking: z.boolean(),
    sessionSamplingRate: z.number(),
    replaySamplingRate: z.number(),
    privacyLevel: z.enum(['allow', 'mask', 'mask-user-input']),
  }),
});
export type ClientConfigType = z.infer<typeof clientConfigSchema>;

const loadEnvConfigFromFile = () => {
  const environment = envVars.VITE_ENVIRONMENT;
  // eslint-disable-next-line no-console
  console.log('Loading client env config from file', environment);

  /**
   * Pattern for config is:
   * key: process.env['KEY'] ?? default
   */
  return {
    version: packageJson.version,
    name: packageJson.name,
    description: packageJson.description ?? '',
    environment: environment,
    locale: 'en-US', // hardcoded for now
    currency: 'USD',
    pricesIncTax: false,
    timeDurationsAreDecimalised: false,
    port: Number(envVars.VITE_PORT) || 3333,
    bffBaseUrl: envVars.VITE_BFF_API_BASE_URL ?? 'http://localhost:4444',
    industryEnablerBaseUrl:
      envVars.VITE_INDUSTRY_ENABLER_BASE_URL ?? 'http://localhost:3444',
    paymentEnablerBaseUrl:
      envVars.PAYMENT_ENABLER_BASE_URL ?? 'http://localhost:7019',
    tireEnablerBaseUrl:
      envVars.VITE_TIRE_ENABLER_BASE_URL ?? 'http://localhost:3444',
    haynesProEnablerBaseUrl:
      envVars.VITE_HAYNES_PRO_ENABLER_BASE_URL ?? 'http://localhost:3444',
    EpicorIntegrationEnabled: envVars.EpicorIntegrationEnabled,
    HaynesProIntegrationEnabled: envVars.HaynesProIntegrationEnabled,
    TireEnablerIntegrationMode: envVars.TireEnablerIntegrationMode,
    RefundsFeatureEnabled: envVars.RefundsFeatureEnabled,
    RefundsV2Enabled: envVars.RefundsV2Enabled,
    RolesAndPermissionsEnabled: envVars.RolesAndPermissionsEnabled,
    datadog: {
      enabled: envVars.DatadogEnabled ?? false,
      environment: envVars.DatadogEnvironment ?? 'unknown',
      serviceName: packageJson.datadog.serviceName,
      applicationId: packageJson.datadog.applicationId,
      clientToken: packageJson.datadog.clientToken,
      allowSessionRecording: envVars.DatadogAllowSessionRecording ?? false,
      allowUserTracking: envVars.DatadogAllowUserTracking ?? false,
      sessionSamplingRate: envVars.DatadogSessionSamplingRate ?? 100,
      replaySamplingRate: envVars.DatadogReplaySamplingRate ?? 50,
      privacyLevel: envVars.DatadogPrivacyLevel ?? 'mask',
    },
  } as ClientConfigType;
};

export const getClientConfig = memoized(() => {
  const envConfig = clientConfigSchema.safeParse(loadEnvConfigFromFile());

  if (!envConfig.success) {
    // eslint-disable-next-line no-console
    console.error(
      '❌ Invalid environment variables: ❌\n',
      envConfig.error.flatten(),
      envConfig
    );
    throw new Error('Invalid environment variables');
  } else {
    // eslint-disable-next-line no-console
    console.table(envConfig.data);
  }

  return envConfig.data;
});

export const clientConfig: ClientConfigType = getClientConfig();
